import { create } from "zustand";

export type ToastSeverity = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
  /** ms before auto-dismiss. Set to 0 or undefined for manual-only. */
  duration?: number;
  /** Optional action button in the toast (e.g. Undo / Retry). */
  action?: { label: string; onClick: () => void };
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  push: (toast) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ toasts: [...s.toasts, { id, duration: 4000, ...toast }] }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/**
 * Convenient imperative API for firing toasts from anywhere (services,
 * mutations, event handlers). Prefer this over reaching into the store
 * directly from component code.
 */
export const toast = {
  success(
    message: string,
    opts: Partial<Omit<Toast, "message" | "severity">> = {},
  ) {
    return useToastStore
      .getState()
      .push({ message, severity: "success", ...opts });
  },
  error(
    message: string,
    opts: Partial<Omit<Toast, "message" | "severity">> = {},
  ) {
    return useToastStore
      .getState()
      .push({ message, severity: "error", duration: 6000, ...opts });
  },
  warning(
    message: string,
    opts: Partial<Omit<Toast, "message" | "severity">> = {},
  ) {
    return useToastStore
      .getState()
      .push({ message, severity: "warning", ...opts });
  },
  info(
    message: string,
    opts: Partial<Omit<Toast, "message" | "severity">> = {},
  ) {
    return useToastStore
      .getState()
      .push({ message, severity: "info", ...opts });
  },
  dismiss(id: string) {
    useToastStore.getState().dismiss(id);
  },
};
